/**
 * ListeningNotice: a verified tag that has not seen a visit yet (Paper
 * "Home · Waiting for first visitor"). Grey, not amber: nothing is broken,
 * the hub is just waiting. Sits between the install state
 * (`TrackingNotInstalledBanner`) and a live site, and clears on its own with
 * the first pageview. Opening the site in a new tab sends that pageview.
 */

import { Button } from "@/components/ui/button"

export interface ListeningSite {
  propertyId: string
  domain: string
}

export default function ListeningNotice({
  sites,
  onOpenSite,
}: {
  sites: ListeningSite[]
  /** Fired when the user opens the site from the notice. */
  onOpenSite?: (site: ListeningSite) => void
}) {
  if (sites.length === 0) return null
  const single = sites.length === 1 ? sites[0] : null
  const title = single
    ? `Listening on ${single.domain}. No pageviews yet.`
    : `Listening on ${sites.length} sites. No pageviews yet.`

  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-xl bg-muted/60 px-4 py-3 shadow-[0_0_0_1px_var(--border)] sm:flex-row sm:items-center"
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
          <span className="size-2 rounded-full bg-foreground/35 shadow-[0_0_0_4px_var(--input)]" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-[13px] font-medium leading-4 text-foreground">{title}</p>
          <p className="text-xs leading-4 text-muted-foreground">
            Every report starts from the first visit, so analytics, heatmaps and tests stay empty until then.
            {single ? " Opening your site in a new tab sends one." : null}
          </p>
        </div>
      </div>
      {single ? (
        <Button
          asChild
          variant="outline"
          className="h-8 shrink-0 self-start rounded-lg px-3 text-[13px] font-medium sm:self-auto"
        >
          <a
            href={`https://${single.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onOpenSite?.(single)}
          >
            Open {single.domain}
          </a>
        </Button>
      ) : null}
    </div>
  )
}
